'use client';

import { useEffect, useState } from "react";
import adminAPI from "../../services/admin/adminAPI";
import ConfirmPopUp from "../popup/confirmPopUp";
import StatusPopUp from "../popup/statusPopUp";
import CreateAdminPopUp, { CreateAdminFormData } from "../popup/createAdminPopUp";
import { RiDeleteBin6Line } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { FaUserPlus } from "react-icons/fa";
import { FaUserEdit } from "react-icons/fa";
import { FaRegUser } from "react-icons/fa";
import { MdKeyboardArrowDown } from "react-icons/md";
import { formatCompact2DateTime } from "../../utils/formatDatetime";

type AdminRow = {
    id: number;
    uuid: string;
    firstname: string;
    lastname: string;
    role: "super_admin" | "admin";
    email: string;
    last_active_at: string;
};

type EditAdminData = {
    id: number;
    firstname: string;
    lastname: string;
    role: "super_admin" | "admin";
    email: string;
};

type ConfirmAction =
    | { type: "create"; payload: CreateAdminFormData }
    | { type: "update"; payload: EditAdminData }
    | { type: "delete"; payload: { id: number; name: string } };

const emptyCreateForm: CreateAdminFormData = {
    username: "",
    password: "",
    firstname: "",
    lastname: "",
    role: "admin",
    email: "",
};

function normalizeAdminRows(response: unknown): AdminRow[] {
    const list = Array.isArray(response)
        ? response
        : Array.isArray((response as { data?: unknown[] })?.data)
            ? (response as { data: unknown[] }).data
            : [];

    return list
        .map((item) => {
            const parsed = item as Partial<AdminRow>;
            if (typeof parsed.id !== "number") return null;

            const role = parsed.role === "super_admin" ? "super_admin" : "admin";

            return {
                id: parsed.id,
                uuid: String(parsed.uuid ?? ""),
                firstname: String(parsed.firstname ?? ""),
                lastname: String(parsed.lastname ?? ""),
                role,
                email: String(parsed.email ?? ""),
                last_active_at: String(parsed.last_active_at ?? ""),
            };
        })
        .filter(Boolean) as AdminRow[];
}

function isFailedResult(response: unknown) {
    return (response as { status?: string })?.status === "failed";
}

function isValidCreateForm(form: CreateAdminFormData) {
    return (
        form.username.trim() &&
        form.password.trim() &&
        form.firstname.trim() &&
        form.lastname.trim() &&
        form.email.trim() &&
        (form.role === "super_admin" || form.role === "admin")
    );
}

function isValidEditForm(form: EditAdminData | null) {
    if (!form) return false;
    return (
        form.firstname.trim() &&
        form.lastname.trim() &&
        form.email.trim() &&
        (form.role === "super_admin" || form.role === "admin")
    );
}

export default function ManageAdmin() {
    const [rows, setRows] = useState<AdminRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [createForm, setCreateForm] = useState<CreateAdminFormData>(emptyCreateForm);
    const [editForm, setEditForm] = useState<EditAdminData | null>(null);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);

    const superAdminCount = rows.filter((item) => item.role === "super_admin").length;
    const adminCount = rows.filter((item) => item.role === "admin").length;

    const fetchAdmins = async () => {
        setIsLoading(true);
        const response = await adminAPI.getAllAdmin();
        if (isFailedResult(response)) {
            setRows([]);
            setStatusPopUp({
                title: "ไม่สำเร็จ",
                message: "โหลดรายการผู้ดูแลระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                type: "error",
            });
            setIsLoading(false);
            return;
        }

        setRows(normalizeAdminRows(response));
        setIsLoading(false);
    };

    useEffect(() => {
        void fetchAdmins();
    }, []);

    const handleOpenCreate = () => {
        setCreateForm(emptyCreateForm);
        setOpenCreate(true);
    };

    const handleOpenEdit = (admin: AdminRow) => {
        setEditForm({
            id: admin.id,
            firstname: admin.firstname,
            lastname: admin.lastname,
            role: admin.role,
            email: admin.email,
        });
    };

    const handleSaveCreateRequest = () => {
        if (!isValidCreateForm(createForm)) {
            setStatusPopUp({
                title: "แจ้งเตือน",
                message: "กรุณากรอกข้อมูลให้ครบถ้วนก่อนบันทึก",
                type: "info",
            });
            return;
        }

        setConfirmAction({
            type: "create",
            payload: {
                username: createForm.username.trim(),
                password: createForm.password.trim(),
                firstname: createForm.firstname.trim(),
                lastname: createForm.lastname.trim(),
                role: createForm.role,
                email: createForm.email.trim(),
            },
        });
    };

    const handleSaveEditRequest = () => {
        if (!isValidEditForm(editForm)) {
            setStatusPopUp({
                title: "แจ้งเตือน",
                message: "กรุณากรอกข้อมูลให้ครบถ้วนก่อนบันทึก",
                type: "info",
            });
            return;
        }

        setConfirmAction({
            type: "update",
            payload: {
                id: editForm!.id,
                firstname: editForm!.firstname.trim(),
                lastname: editForm!.lastname.trim(),
                role: editForm!.role,
                email: editForm!.email.trim(),
            },
        });
    };

    const handleDeleteRequest = (admin: AdminRow) => {
        setConfirmAction({
            type: "delete",
            payload: {
                id: admin.id,
                name: `${admin.firstname} ${admin.lastname}`.trim() || admin.email,
            },
        });
    };

    const executeConfirm = async () => {
        if (!confirmAction) return;

        setIsSubmitting(true);

        if (confirmAction.type === "create") {
            const response = await adminAPI.createAdmin(confirmAction.payload);
            if (isFailedResult(response)) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "เพิ่มผู้ดูแลระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                    type: "error",
                });
                setIsSubmitting(false);
                return;
            }

            setOpenCreate(false);
            setCreateForm(emptyCreateForm);
            setStatusPopUp({
                title: "สำเร็จ",
                message: "เพิ่มผู้ดูแลระบบสำเร็จ",
                type: "success",
            });
        }

        if (confirmAction.type === "update") {
            const { id, firstname, lastname, role, email } = confirmAction.payload;
            const response = await adminAPI.updateAdmin(id, {
                firstname,
                lastname,
                role,
                email,
            });

            if (isFailedResult(response)) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "แก้ไขข้อมูลผู้ดูแลระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                    type: "error",
                });
                setIsSubmitting(false);
                return;
            }

            setEditForm(null);
            setStatusPopUp({
                title: "สำเร็จ",
                message: "แก้ไขข้อมูลผู้ดูแลระบบสำเร็จ",
                type: "success",
            });
        }

        if (confirmAction.type === "delete") {
            const response = await adminAPI.softDeleteAdmin(confirmAction.payload.id);
            if (isFailedResult(response)) {
                setStatusPopUp({
                    title: "ไม่สำเร็จ",
                    message: "ลบผู้ดูแลระบบไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
                    type: "error",
                });
                setIsSubmitting(false);
                return;
            }

            setStatusPopUp({
                title: "สำเร็จ",
                message: "ลบผู้ดูแลระบบสำเร็จ",
                type: "success",
            });
        }

        setConfirmAction(null);
        setIsSubmitting(false);
        await fetchAdmins();
    };

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-wrap gap-8">
                    <div className="h-32.5 w-62.5 rounded-md bg-[#ffff] shadow-md px-5 py-4">
                        <div className="flex items-center gap-5">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#5CD039]">
                                <FaRegUser className="text-2xl text-white" />
                            </div>
                            <p className="text-xl leading-none font-normal text-[#2b2b2b]">Super Admin</p>
                        </div>

                        <div className="mt-2 flex items-end justify-between">
                            <div className="w-full text-center text-3xl font-bold leading-none text-[#0092D7]">
                                {superAdminCount}
                            </div>
                            <p className="pl-3 text-lg font-normal leading-none text-[#4E4E4E]">คน</p>
                        </div>
                    </div>

                    <div className="h-32.5 w-62.5 rounded-md bg-[#ffff] shadow-md px-5 py-4">
                        <div className="flex items-center gap-5">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#7BC8FF]">
                                <FaRegUser className="text-2xl text-white" />
                            </div>
                            <p className="text-xl leading-none font-normal text-[#2b2b2b]">Admin</p>
                        </div>

                        <div className="mt-2 flex items-end justify-between">
                            <div className="w-full text-center text-3xl font-bold leading-none text-[#0092D7]">
                                {adminCount}
                            </div>
                            <p className="pl-3 text-lg font-normal leading-none text-[#4E4E4E]">คน</p>
                        </div>
                    </div>
                </div>

                <div className="flex min-h-32.5 items-end">
                    <button
                        type="button"
                        onClick={handleOpenCreate}
                        className="flex items-center gap-1.5 rounded-md bg-[#0092D7] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#007bb5] cursor-pointer"
                    >
                        <FaUserPlus className="text-lg font-bold leading-none" />
                        เพิ่มผู้ดูแลระบบ
                    </button>
                </div>
            </div>

            <div className="bg-white pb-4">
                <div className="px-4 py-4 text-lg font-bold text-[#555555]">
                    ผู้ดูแลระบบ
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full border-collapse">
                        <thead>
                            <tr className="bg-[#F8F8F8]">
                                <th className="w-12 px-4 py-3 text-md font-semibold text-[#4e4e4e] text-left">#</th>
                                <th className="px-4 py-3 text-md font-semibold text-[#4e4e4e] text-left">ชื่อ</th>
                                <th className="px-4 py-3 text-md font-semibold text-[#4e4e4e] text-left">นามสกุล</th>
                                <th className="px-4 py-3 text-md font-semibold text-[#4e4e4e] text-left pl-14">Role</th>
                                <th className="px-4 py-3 text-md font-semibold text-[#4e4e4e] text-left">อีเมล</th>
                                <th className="px-4 py-3 text-md font-semibold text-[#4e4e4e] text-left">ใช้งานล่าสุด</th>
                                <th className="px-4 py-3 text-md font-semibold text-[#4e4e4e]"> </th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-lg text-[#9a9a9a]">
                                        กำลังโหลดข้อมูล...
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-lg text-[#9a9a9a]">
                                        ไม่พบข้อมูลผู้ดูแลระบบ
                                    </td>
                                </tr>
                            ) : (
                                rows.map((admin, index) => (
                                    <tr key={admin.id} className={index % 2 === 0 ? "bg-[#EEEFF6]" : "bg-[#F8F8F8]"}>
                                        <td className="px-4 py-2 text-md text-[#555555]">{index + 1}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{admin.firstname || "-"}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{admin.lastname || "-"}</td>
                                        <td className="px-4 py-2 text-md">
                                            <span
                                                className={`inline-flex w-[120px] items-center justify-center rounded-full px-2 py-1.5 text-md font-normal ${admin.role === "super_admin"
                                                    ? "bg-[#DBF0E5] text-[#3C9065]"
                                                    : "bg-[#7BC8FF]/40 text-[#0092D7]"
                                                    }`}
                                            >
                                                {admin.role === "super_admin" ? "Super admin" : "Admin"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{admin.email || "-"}</td>
                                        <td className="px-4 py-2 text-md text-[#555555]">{formatCompact2DateTime(admin.last_active_at)}</td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleOpenEdit(admin)}
                                                    className="inline-flex min-w-[85px] cursor-pointer items-center justify-center gap-1.5 rounded-md bg-[#0092D7] hover:bg-[#007bb5] px-3 py-1.5 text-xs font-medium text-white transition"
                                                >
                                                    <FaUserEdit className="text-md" />
                                                    แก้ไขข้อมูล
                                                </button>

                                                {admin.role !== "super_admin" ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteRequest(admin)}
                                                        className="inline-flex min-w-[60px] cursor-pointer items-center justify-center gap-1.5 rounded-md bg-[#F54B4B] hover:bg-[#e64545] px-4 py-1.5 text-xs font-medium text-white transition"
                                                    >
                                                        <RiDeleteBin6Line className="text-md" />
                                                        ลบ
                                                    </button>
                                                ) : null}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {openCreate ? (
                <CreateAdminPopUp
                    onClose={() => setOpenCreate(false)}
                    formData={createForm}
                    onChange={setCreateForm}
                    onSave={handleSaveCreateRequest}
                    isSaving={isSubmitting}
                />
            ) : null}

            {editForm ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
                    <div className="w-full max-w-130 rounded-md bg-[#f3f3f3] p-4 shadow-[0_6px_20px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold leading-none text-[#4e4e4e]">แก้ไขข้อมูลผู้ดูแลระบบ</h2>
                            <button
                                type="button"
                                onClick={() => setEditForm(null)}
                                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-[#d7dade] text-[#8d939a]"
                            >
                                <IoClose size={20} />
                            </button>
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-3">
                            <div>
                                <label className="block text-base font-semibold text-[#5a5a5a]">Firstname</label>
                                <input
                                    type="text"
                                    value={editForm.firstname}
                                    onChange={(event) =>
                                        setEditForm({
                                            ...editForm,
                                            firstname: event.target.value,
                                        })
                                    }
                                    className="mt-1 h-11 w-full rounded-sm border border-[#d5d5d5] bg-white px-3 text-base text-[#4E4E4E] outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-base font-semibold text-[#5a5a5a]">Lastname</label>
                                <input
                                    type="text"
                                    value={editForm.lastname}
                                    onChange={(event) =>
                                        setEditForm({
                                            ...editForm,
                                            lastname: event.target.value,
                                        })
                                    }
                                    className="mt-1 h-11 w-full rounded-sm border border-[#d5d5d5] bg-white px-3 text-base text-[#4E4E4E] outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-base font-semibold text-[#5a5a5a]">Email</label>
                                <input
                                    type="email"
                                    value={editForm.email}
                                    onChange={(event) =>
                                        setEditForm({
                                            ...editForm,
                                            email: event.target.value,
                                        })
                                    }
                                    className="mt-1 h-11 w-full rounded-sm border border-[#d5d5d5] bg-white px-3 text-base text-[#4E4E4E] outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-base font-semibold text-[#5a5a5a]">Role</label>
                                <div className="relative mt-1">
                                    <select
                                        value={editForm.role}
                                        onChange={(event) =>
                                            setEditForm({
                                                ...editForm,
                                                role: event.target.value as "super_admin" | "admin",
                                            })
                                        }
                                        className="h-11 w-full appearance-none rounded-sm border border-[#d5d5d5] bg-white px-3 pr-10 text-base text-[#4E4E4E] outline-none cursor-pointer"
                                    >
                                        {/* <option value="super_admin">Super Admin</option> */}
                                        <option value="admin">Admin</option>
                                    </select>
                                    <MdKeyboardArrowDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-2xl text-[#9a9a9a]" />
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setEditForm(null)}
                                disabled={isSubmitting}
                                className="rounded-md bg-transparent px-3 py-1 text-base font-semibold text-[#1c8bc7] transition hover:underline cursor-pointer"
                            >
                                ยกเลิก
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveEditRequest}
                                disabled={isSubmitting}
                                className="min-w-24 cursor-pointer rounded-md bg-[#0a9cf0] px-5 py-2 text-base font-semibold text-white hover:bg-[#0784b9] disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                {isSubmitting ? "กำลังบันทึก..." : "บันทึก"}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {confirmAction ? (
                <ConfirmPopUp
                    title={
                        confirmAction.type === "delete"
                            ? "ยืนยันการลบ"
                            : confirmAction.type === "update"
                                ? "ยืนยันการแก้ไข"
                                : "ยืนยันการเพิ่ม"
                    }
                    message={
                        confirmAction.type === "delete"
                            ? `ต้องการลบ \"${confirmAction.payload.name}\" ใช่หรือไม่?`
                            : confirmAction.type === "update"
                                ? "ต้องการบันทึกการแก้ไขข้อมูลผู้ดูแลระบบใช่หรือไม่?"
                                : "ต้องการเพิ่มผู้ดูแลระบบนี้ใช่หรือไม่?"
                    }
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={executeConfirm}
                    isConfirming={isSubmitting}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={() => setStatusPopUp(null)}
                />
            ) : null}
        </div>
    );
}